import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Team,
  Player,
  Match,
  SetRecord,
  Rally,
  MatchState,
  TossChoice,
  MatchType,
  TeamState,
  RotationState,
  TournamentRules,
  getMatchRules,
  MatchRulesConfig,
  CategoryAssignment,
  Timeout,
  LostReason,
} from '@/types';
import { recomputeMatchState, computedToMatchState, MatchStateInput, DbServingState } from '@/lib/matchState';
import { supabase, supabaseConfigValid, getTeams, updateMatchStatus, setMatchStartTime, createSet, updateSet, updateSetServingState, createRally, deleteRally, getMatchWithDetails, getRalliesBySet, discardMatch as discardMatchInDB, getTournament, createTimeout, endTimeout, getActiveTimeout, updateSetSideChange, getCompletedTimeouts, getSetByMatchAndNumber, SERVER_FAULT_PLAYER_ID } from '@/lib/supabase';
import {
  getFirstServingTeam,
  getEligibleServers,
  validateRotationGap,
  validateFirstServerTeam,
  validateServerTeam,
  checkRotationDeadlock,
} from '@/lib/validation';
import { saveMatchRef, loadMatchRef, clearMatchRef, PersistedMatchRef } from '@/lib/persistence';
import { useAuth } from '@/lib/auth';
import { capturePageView, capture } from '@/lib/analytics';
import Login from '@/components/screens/Login';
import ProtectedRoute from '@/components/ProtectedRoute';
import FirstServerSelection from '@/components/screens/FirstServerSelection';
import LiveScoring from '@/components/screens/LiveScoring';
import MatchComplete from '@/components/screens/MatchComplete';
import AdminDashboard from '@/components/screens/AdminDashboard';
import AdminPanel from '@/components/screens/AdminPanel';
import UserManagement from '@/components/screens/UserManagement';
import ClubSettings from '@/components/screens/ClubSettings';
import TournamentManagement from '@/components/screens/TournamentManagement';
import TournamentDashboard from '@/components/screens/TournamentDashboard';
import ScorerDashboard from '@/components/screens/ScorerDashboard';
import AdminMatchManagement from '@/components/screens/AdminMatchManagement';
import PublicTournamentCentre from '@/components/screens/PublicTournamentCentre';
import PublicPlayerProfile from '@/components/screens/PublicPlayerProfile';
import BroadcastOverlay from '@/components/screens/BroadcastOverlay';
import BroadcastLiveOverlay from '@/components/screens/BroadcastLiveOverlay';
import BroadcastControl from '@/components/screens/BroadcastControl';
import RallyEditor from '@/components/screens/RallyEditor';
import AppHeader from '@/components/AppHeader';
import DevBanner from '@/components/DevBanner';

interface ResumeMatchScreenProps {
  matchState: MatchState;
  servingTeam: TeamState | null;
  currentServer: Player | null;
  eligibleServers: any;
  teams: Team[];
  recoveryLoading: boolean;
  recoverMatch: (matchId?: string) => Promise<void>;
  resetMatch: () => void;
  recordRally: (winningTeamId: string, categories?: CategoryAssignment[], isServerFault?: boolean) => Promise<void>;
  selectNextServer: (player: Player) => Promise<void>;
  undoLastRally: () => Promise<void>;
  undoServerSelection: () => Promise<void>;
  canUndoServerSelection: () => { canUndo: boolean; servingTeamId: string | null };
  startNewSet: (firstServerId: string) => Promise<void>;
  recordServeFoul: (server: Player) => Promise<void>;
  recordServerFault: () => Promise<void>;
  fetchSetRallies: (setId: string) => Promise<Rally[]>;
  selectFirstServer: (player: Player) => Promise<void>;
  signOut: () => void;
  onExitToTournament: (tournamentId: string) => void;
  // Timeout props
  activeTimeout: Timeout | null;
  startTimeout: (teamId: string) => Promise<void>;
  endTimeout: () => Promise<void>;
  completedTimeouts: Timeout[];
  lastTimeoutDuration: number | null;
}

type ResumePhase = 'loading-info' | 'recovery-ui' | 'resuming' | 'scoring' | 'error';

function ResumeMatchScreen({
  matchState, servingTeam, currentServer, eligibleServers, teams,
  recoveryLoading, recoverMatch, resetMatch, recordRally,
  selectNextServer, undoLastRally, undoServerSelection, canUndoServerSelection,
  startNewSet, recordServeFoul, recordServerFault, fetchSetRallies, selectFirstServer, signOut,
  onExitToTournament, activeTimeout, startTimeout, endTimeout, completedTimeouts, lastTimeoutDuration,
}: ResumeMatchScreenProps) {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { isAdmin, profile } = useAuth();
  const isViewer = profile?.role === 'viewer';
  const [phase, setPhase] = useState<ResumePhase>('loading-info');
  const [matchInfo, setMatchInfo] = useState<{ matchNumber: number; status: string; teamAId: string; teamBId: string } | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load minimal match info for the recovery UI display
  useEffect(() => {
    if (!matchId) { setPhase('error'); setRecoveryError('No match ID provided'); return; }
    console.log('[ResumeMatch] Loading match info for:', matchId);
    getMatchWithDetails(matchId)
      .then(data => {
        console.log('[ResumeMatch] Match info loaded:', data.status);
        if (data.status === 'discarded') {
          clearMatchRef();
          setPhase('error');
          setRecoveryError('This match has been discarded.');
          return;
        }
        setMatchInfo({
          matchNumber: data.match_number,
          status: data.status,
          teamAId: data.team_a_id,
          teamBId: data.team_b_id,
          ...(data.tournament_id ? { tournamentId: data.tournament_id } : {}),
        } as any);
        // Viewers skip the recovery UI and go straight to resuming
        setPhase(isViewer ? 'resuming' : 'recovery-ui');
      })
      .catch(err => {
        console.error('[ResumeMatch] Failed to load match info:', err);
        setPhase('error');
        setRecoveryError(err instanceof Error ? err.message : 'Failed to load match details');
      });
  }, [matchId]);

  // Trigger recovery when entering resuming phase (both viewers auto-skipped to this and scorers/admins via button)
  useEffect(() => {
    if (phase === 'resuming' && teams.length > 0 && matchId) {
      console.log('[ResumeMatch] Starting recovery for:', matchId);
      setRecoveryError(null);
      recoverMatch(matchId)
        .then(() => {
          console.log('[ResumeMatch] Recovery complete, transitioning to scoring');
          setPhase('scoring');
        })
        .catch(err => {
          console.error('[ResumeMatch] Recovery failed:', err);
          setRecoveryError(err instanceof Error ? err.message : 'Recovery failed');
          setPhase('error');
        });
    }
  }, [phase, teams.length, matchId]);

  const handleResume = () => {
    if (!matchId) return;
    setPhase('resuming');
  };

  const handleDiscard = async () => {
    if (!matchId) return;
    setDiscarding(true);
    try {
      await discardMatchInDB(matchId);
      clearMatchRef();
      resetMatch();
      const tid = (matchInfo as any)?.tournamentId;
      if (tid) navigate(`/tournament/${tid}`, { replace: true });
      else navigate(isAdmin ? '/admin/matches' : '/tournaments', { replace: true });
    } catch (err) {
      console.error('[handleDiscard] Failed:', err);
      setDiscarding(false);
      setShowDiscardConfirm(false);
    }
  };

  const handleBackToTournament = () => {
    const tid = (matchInfo as any)?.tournamentId;
    if (tid) navigate(`/tournament/${tid}`);
    else navigate(isAdmin ? '/admin' : '/tournaments');
  };

  const teamA = teams.find(t => t.id === matchInfo?.teamAId);
  const teamB = teams.find(t => t.id === matchInfo?.teamBId);

  // Once scoring is active (recoverMatch resolved and matchState loaded), show the scoring screens
  const isScoring = phase === 'scoring' || (phase === 'resuming' && !recoveryLoading && matchState.status !== 'setup');

  // Viewer auto-refresh: poll every 5 seconds when live
  const handleViewerRefresh = useCallback(async () => {
    if (!matchId) return;
    try {
      await recoverMatch(matchId);
      setLastRefreshed(new Date());
    } catch {
      // silently ignore poll errors
    }
  }, [matchId, recoverMatch]);

  const isLiveMatch = matchState.status === 'live' || matchState.status === 'set-won';

  useEffect(() => {
    if (!isViewer || !isScoring || !isLiveMatch) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(handleViewerRefresh, 5000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [isViewer, isScoring, isLiveMatch, handleViewerRefresh]);

  if (phase === 'loading-info') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-text-secondary text-sm">Loading match info...</p>
      </div>
    );
  }

  if (phase === 'error' || (!matchInfo && phase !== 'resuming' && phase !== 'scoring')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Could Not Load Match</h1>
          <p className="text-text-secondary mb-4 text-sm">
            {recoveryError || 'The match may have been discarded, or you may not have access.'}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setRecoveryError(null);
                setPhase('loading-info');
              }}
              className="w-full py-3 px-6 rounded-lg font-bold bg-primary text-white hover:opacity-90 transition-all"
            >
              Retry
            </button>
            <button
              onClick={() => navigate(isAdmin ? '/admin' : '/tournaments')}
              className="w-full py-3 px-6 rounded-lg font-semibold bg-surface border border-border text-text-primary hover:bg-surface-raised transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'resuming' && (recoveryLoading || matchState.status === 'setup')) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-text-secondary text-sm">
          {recoveryLoading ? 'Recovering match state...' : 'Initializing...'}
        </p>
      </div>
    );
  }

  if (isScoring) {
    if (matchState.status === 'first-server' && !isViewer) {
      return (
        <FirstServerSelection
          matchState={matchState}
          teams={teams}
          onSelectServer={selectFirstServer}
          onSignOut={signOut}
          readOnly={false}
        />
      );
    }
    if (matchState.status === 'match-won') {
      return (
        <MatchComplete
          matchState={matchState}
          teams={teams}
          onNewMatch={resetMatch}
          onSignOut={signOut}
          readOnly={isViewer}
        />
      );
    }
    if (matchState.status !== 'setup') {
      const tid = (matchInfo as any)?.tournamentId as string | undefined;
      return (
        <LiveScoring
          matchState={matchState}
          servingTeam={servingTeam}
          currentServer={currentServer}
          eligibleServers={eligibleServers}
          serverSelectionNeeded={!!matchState.currentSet && !matchState.currentSet.currentServerId}
          onRecordRally={recordRally}
          onSelectNextServer={selectNextServer}
          onUndo={undoLastRally}
          onUndoServerSelection={undoServerSelection}
          canUndoServerSelection={canUndoServerSelection}
          onStartNewSet={startNewSet}
          onRecordServeFoul={recordServeFoul}
          onRecordServerFault={recordServerFault}
          onFetchSetRallies={fetchSetRallies}
          onSignOut={signOut}
          tournamentId={tid}
          onExitToTournament={tid ? onExitToTournament : undefined}
          readOnly={isViewer}
          onRefresh={isViewer ? handleViewerRefresh : undefined}
          lastRefreshed={isViewer ? lastRefreshed : undefined}
          activeTimeout={activeTimeout}
          onStartTimeout={startTimeout}
          onEndTimeout={endTimeout}
          completedTimeouts={completedTimeouts}
          lastTimeoutDuration={lastTimeoutDuration}
        />
      );
    }
  }

  // Recovery UI
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={signOut}
        tournamentId={(matchInfo as any)?.tournamentId ?? undefined}
        navIcon={isAdmin ? 'admin' : 'home'}
        onNavigate={handleBackToTournament}
      />

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Active Match Card */}
          <div className="bg-warning/10 border border-warning/30 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-3 h-3 rounded-full bg-warning animate-pulse" />
              <h2 className="text-sm font-bold text-warning uppercase tracking-wider">Active Match Found</h2>
            </div>

            {teamA && teamB ? (
              <div className="bg-surface rounded-lg border border-border p-4 mb-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-text-muted uppercase">Match</span>
                  <span className="text-sm font-bold text-text-primary">#{matchInfo?.matchNumber}</span>
                </div>
                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: teamA.color }} />
                    <span className="text-sm font-bold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                  </div>
                  <span className="text-text-muted text-sm font-bold">VS</span>
                  <div className="text-center">
                    <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: teamB.color }} />
                    <span className="text-sm font-bold" style={{ color: teamB.color }}>{teamB.shortName}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-text-muted uppercase">Status</span>
                  <span className="text-xs font-semibold text-warning uppercase">{matchInfo?.status.replace('-', ' ')}</span>
                </div>
              </div>
            ) : (
              <div className="bg-surface rounded-lg border border-border p-4 mb-1">
                <p className="text-sm text-text-muted text-center">Match #{matchInfo?.matchNumber}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleResume}
              disabled={teams.length === 0}
              className="w-full py-4 px-6 rounded-xl font-bold text-base bg-success text-white transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-60"
            >
              {teams.length === 0 ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  LOADING...
                </span>
              ) : isViewer ? 'VIEW MATCH' : 'RESUME MATCH'}
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowDiscardConfirm(true)}
                className="w-full py-4 px-6 rounded-xl font-bold text-base bg-surface border-2 border-error text-error transition-all active:scale-[0.98] hover:bg-error/5"
              >
                DISCARD MATCH
              </button>
            )}
            <button
              onClick={handleBackToTournament}
              className="w-full py-3 px-6 rounded-xl font-semibold text-sm bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] hover:bg-surface-raised"
            >
              {(matchInfo as any)?.tournamentId ? 'BACK TO TOURNAMENT' : isAdmin ? 'BACK TO ADMIN' : 'BACK TO DASHBOARD'}
            </button>
          </div>
        </div>
      </div>

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-text-primary mb-2">Discard Match?</h3>
            <p className="text-sm text-text-secondary mb-6">
              This will permanently delete Match #{matchInfo?.matchNumber} and all its rally data. This cannot be undone.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all active:scale-[0.98] disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleDiscard}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-error text-white transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {discarding ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DELETING...
                  </span>
                ) : 'DISCARD'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, configError } = useAuth();

  // Track page views on every route change
  useEffect(() => {
    capturePageView(location.pathname);
  }, [location.pathname]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [matchState, setMatchState] = useState<MatchState>({
    match: null,
    teamA: null,
    teamB: null,
    currentSetNumber: 0,
    currentSet: null,
    status: 'setup',
    sets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recoveryRef, setRecoveryRef] = useState<PersistedMatchRef | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const autoSaveEnabled = useRef(false);
  const initialLoadDone = useRef(false);
  const undoInProgress = useRef(false);
  const rallyInProgress = useRef(false);
  const serverSelectionSnapshots = useRef<Map<string, { rallyCount: number; previousRotationState: RotationState }>>(new Map());

  // Timeout state
  const [activeTimeout, setActiveTimeout] = useState<Timeout | null>(null);
  const [completedTimeouts, setCompletedTimeouts] = useState<Timeout[]>([]);
  const [lastTimeoutDuration, setLastTimeoutDuration] = useState<number | null>(null);

  // Side change state
  const [showSideChangeNotice, setShowSideChangeNotice] = useState(false);
  const [sideChangeProcessed, setSideChangeProcessed] = useState(false);

  // First server team selection state (for new sets)
  const [pendingFirstServerTeamId, setPendingFirstServerTeamId] = useState<string | null>(null);

  // Get match rules config from current match state
  const getMatchRulesConfig = useCallback((): MatchRulesConfig | null => {
    if (!matchState.match) return null;
    return getMatchRules(
      matchState.match.matchType,
      matchState.tournamentRules || null,
      matchState.match.winningPoint,
      matchState.match.goldenPoint
    );
  }, [matchState.match, matchState.tournamentRules]);

  // Load teams + check for recoverable match on mount
  useEffect(() => {
    loadTeams();
  }, []);

  // Auto-save: persist match ref whenever matchState changes (after initial load)
  useEffect(() => {
    if (!autoSaveEnabled.current) return;
    const { match, status } = matchState;
    if (match && match.teamAId && match.teamBId && status !== 'setup') {
      saveMatchRef({
        matchId: match.id,
        teamAId: match.teamAId,
        teamBId: match.teamBId,
        status,
        tournamentId: match.tournamentId,
      });
    }
    if (status === 'setup' && !match) {
      clearMatchRef();
    }
  }, [matchState]);

  // Poll DB match status to detect external changes (e.g. admin reset current set)
  useEffect(() => {
    const matchId = matchState.match?.id;
    if (!matchId || matchState.status === 'setup' || matchState.status === 'match-won') return;

    const checkStatus = async () => {
      if (!supabaseConfigValid) return;
      try {
        const { data, error } = await supabase
          .from('matches')
          .select('status')
          .eq('id', matchId)
          .single();
        if (error || !data) return;
        // If DB status changed to 'set-won' while we think we're live/timeout/first-server,
        // an admin likely reset the current set. Trigger recovery.
        if (data.status === 'set-won' && matchState.status !== 'set-won' && matchState.status !== 'match-won') {
          console.log('[Status Poll] Detected admin reset, recovering match...');
          await recoverMatch(matchId);
        }
      } catch (e) {
        // Silently ignore polling errors
      }
    };

    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [matchState.match?.id, matchState.status]);

  const formatTeams = (data: Array<{ id: string; name: string; short_name: string; color: string; logo_url?: string | null; players: Array<{ id: string; name: string; number: number; team_id: string }> }>): Team[] =>
    data.map(t => ({
      id: t.id,
      name: t.name,
      shortName: t.short_name,
      color: t.color,
      logoUrl: t.logo_url,
      players: t.players.map(p => ({
        id: p.id,
        name: p.name,
        number: p.number,
        teamId: p.team_id,
      })),
    }));

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await getTeams();
      setTeams(formatTeams(data));

      // Check for recoverable match — verify it's still live in DB
      const ref = loadMatchRef();
      if (ref && ref.status !== 'match-won') {
        try {
          const matchData = await getMatchWithDetails(ref.matchId);
          if (matchData.status === 'discarded' || matchData.status === 'match-won') {
            clearMatchRef();
          } else {
            setRecoveryRef(ref);
          }
        } catch {
          // Match no longer exists; clear stale ref
          clearMatchRef();
        }
      }
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
      autoSaveEnabled.current = true;
    }
  };

  const reloadTeams = async () => {
    try {
      const data = await getTeams();
      setTeams(formatTeams(data));
    } catch (err) {
      console.error('Failed to reload teams:', err);
    }
  };

  // Check for side change in deciding set
  useEffect(() => {
    if (!matchState.match || !matchState.currentSet || matchState.status !== 'live') return;

    const rules = getMatchRulesConfig();
    if (!rules) return;

    // Check if this is a deciding set (last set)
    const maxSets = rules.maxSets;
    const currentSetNumber = matchState.currentSetNumber;

    // Deciding set is the final possible set
    // For best of 3: Set 3 is deciding
    // For best of 5: Set 5 is deciding
    const isDecidingSet = currentSetNumber === maxSets;

    if (!isDecidingSet) return;

    const { scoreA, scoreB } = matchState.currentSet;
    const winningPoint = rules.winningPoint;
    const midpoint = Math.ceil(winningPoint / 2);

    // Check if both teams have reached at least the midpoint
    const reachedSideChange = scoreA >= midpoint && scoreB >= midpoint;

    if (reachedSideChange && !sideChangeProcessed && !showSideChangeNotice) {
      // Check if side change has already been shown for this set
      const currentSetRecord = matchState.sets.find(s => !s.endedAt);
      if (currentSetRecord && !currentSetRecord.sideChangeShown) {
        setShowSideChangeNotice(true);
      }
    }
  }, [matchState.currentSet, matchState.status, matchState.sets, sideChangeProcessed, showSideChangeNotice, getMatchRulesConfig, matchState.match]);

  const fetchSetRallies = useCallback(async (setId: string): Promise<Rally[]> => {
    const data = await getRalliesBySet(setId);
    return data.map((r: {
      id: string; set_id: string; rally_number: number;
      serving_team_id: string; server_id: string;
      point_won_by_id: string; lost_reason: string | null;
      point_winner_id: string | null; recorded_at: string;
      categories?: CategoryAssignment[];
      is_server_fault?: boolean;
    }) => ({
      id: r.id,
      setId: r.set_id,
      rallyNumber: r.rally_number,
      servingTeamId: r.serving_team_id,
      serverId: r.server_id,
      pointWonById: r.point_won_by_id,
      lostReason: r.lost_reason as LostReason,
      pointWinnerId: r.point_winner_id,
      recordedAt: r.recorded_at,
      categories: r.categories || [],
      isServerFault: r.is_server_fault || false,
    }));
  }, []);

  const recoverMatch = useCallback(async (overrideMatchId?: string) => {
    const targetId = overrideMatchId ?? recoveryRef?.matchId;
    if (!targetId) throw new Error('No match ID');
    if (teams.length === 0) throw new Error('Teams not loaded yet');

    if (!recoveryRef || recoveryRef.matchId !== targetId) {
      setRecoveryRef({ matchId: targetId, teamAId: '', teamBId: '', status: 'live' });
    }
    setRecoveryLoading(true);

    try {
      // 1. Fetch complete match data from DB
      const data = await getMatchWithDetails(targetId);
      if (data.status === 'discarded') {
        clearMatchRef();
        setRecoveryRef(null);
        setRecoveryLoading(false);
        return;
      }

      // 2. Find teams
      const teamA = teams.find(t => t.id === data.team_a_id);
      const teamB = teams.find(t => t.id === data.team_b_id);
      if (!teamA || !teamB) {
        clearMatchRef();
        setRecoveryRef(null);
        setRecoveryLoading(false);
        return;
      }

      // 3. Build match object
      const formattedMatch: Match = {
        id: data.id,
        matchNumber: data.match_number,
        date: data.date,
        matchTime: data.match_time ?? null,
        matchType: data.match_type as MatchType,
        teamAId: data.team_a_id,
        teamBId: data.team_b_id,
        tossWonById: data.toss_won_by_id ?? null,
        tossChoice: (data.toss_choice as TossChoice) ?? null,
        status: data.status as MatchState['status'],
        winnerId: data.winner_id,
        tournamentId: data.tournament_id,
        winningPoint: data.winning_point,
        goldenPoint: data.golden_point,
        startTime: data.start_time ?? null,
        endTime: data.end_time ?? null,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      // 4. Fetch tournament rules
      let tournamentRules: TournamentRules | null = null;
      if (data.tournament_id) {
        try {
          const tournament = await getTournament(data.tournament_id);
          tournamentRules = {
            leagueWinningPoint: tournament.leagueWinningPoint,
            leagueGoldenPoint: tournament.leagueGoldenPoint,
            playoffWinningPoint: tournament.playoffWinningPoint,
            playoffGoldenPoint: tournament.playoffGoldenPoint,
          };
        } catch (err) {
          console.error('[RECOVERY] Failed to fetch tournament (non-fatal):', err);
        }
      }

      // 5. Parse DB sets + rallies into SetRecord[]
      const sets: SetRecord[] = (data.sets || []).map((s: any) => ({
        id: s.id,
        matchId: s.match_id,
        setNumber: s.set_number,
        scoreA: s.score_a,
        scoreB: s.score_b,
        winnerId: s.winner_id,
        startedAt: s.started_at,
        endedAt: s.ended_at,
        rallies: (s.rallies || []).map((r: any) => ({
          id: r.id,
          setId: r.set_id,
          rallyNumber: r.rally_number,
          servingTeamId: r.serving_team_id,
          serverId: r.server_id,
          pointWonById: r.point_won_by_id,
          lostReason: r.lost_reason as LostReason,
          pointWinnerId: r.point_winner_id,
          recordedAt: r.recorded_at,
          categories: r.categories || [],
          isServerFault: r.is_server_fault || false,
        })).sort((a: Rally, b: Rally) => b.rallyNumber - a.rallyNumber),
        sideChangeShown: s.side_change_shown || false,
      }));

      // 6. Identify current set and its rallies
      const currentSetRecord = sets.find(s => s.endedAt === null) ?? sets[sets.length - 1] ?? null;
      const currentSetRallies = currentSetRecord?.rallies ?? [];

      // 7. DB serving columns for the current set
      const dbServingState: DbServingState = {
        servingTeamId: currentSetRecord ? (currentSetRecord as any).current_serving_team_id ?? null : null,
        serverId: currentSetRecord ? (currentSetRecord as any).current_server_id ?? null : null,
      };

      // 8. Fetch timeouts (active + completed)
      const [activeTimeoutResult, completedTimeoutsResult] = await Promise.all([
        getActiveTimeout(formattedMatch.id),
        getCompletedTimeouts(formattedMatch.id),
      ]);
      const allTimeouts: Timeout[] = [...completedTimeoutsResult];
      if (activeTimeoutResult) allTimeouts.push(activeTimeoutResult);

      // 9. Build recompute input
      const recomputeInput: MatchStateInput = {
        match: formattedMatch,
        teamA: {
          id: teamA.id,
          name: teamA.name,
          shortName: teamA.shortName,
          color: teamA.color,
          logoUrl: teamA.logoUrl,
          players: teamA.players,
        },
        teamB: {
          id: teamB.id,
          name: teamB.name,
          shortName: teamB.shortName,
          color: teamB.color,
          logoUrl: teamB.logoUrl,
          players: teamB.players,
        },
        sets,
        currentSetRallies,
        tournamentRules,
        timeouts: allTimeouts,
        dbServingState,
        activeTimeout: activeTimeoutResult,
      };

      // 10. Run the pure recompute engine — the ONLY source of truth
      const computed = recomputeMatchState(recomputeInput);

      // 11. Persist corrected computed values back to DB (only if different)
      if (currentSetRecord && computed.currentSet) {
        const dbScoreA = currentSetRecord.scoreA;
        const dbScoreB = currentSetRecord.scoreB;
        const dbWinnerId = currentSetRecord.winnerId;
        const dbEndedAt = currentSetRecord.endedAt;

        const computedScoreA = computed.currentSet.scoreA;
        const computedScoreB = computed.currentSet.scoreB;
        const computedWinnerId = computed.currentSet.setWinnerId;
        const computedEndedAt = computed.currentSet.endedAt;

        const needsUpdate =
          dbScoreA !== computedScoreA ||
          dbScoreB !== computedScoreB ||
          dbWinnerId !== computedWinnerId ||
          (computedWinnerId && !dbEndedAt);

        if (needsUpdate) {
          await updateSet(currentSetRecord.id, {
            scoreA: computedScoreA,
            scoreB: computedScoreB,
            winnerId: computedWinnerId,
            endedAt: computedEndedAt,
          });
        }
      }

      const dbStatus = data.status;
      const dbMatchWinnerId = data.winner_id;
      const dbEndTime = data.end_time;
      const computedStatus = computed.status;
      const computedMatchWinnerId = computed.matchWinnerId;
      const computedEndTime = computedStatus === 'match-won' ? (dbEndTime ?? new Date().toISOString()) : undefined;

      const needsMatchUpdate =
        dbStatus !== computedStatus ||
        dbMatchWinnerId !== computedMatchWinnerId ||
        (computedStatus === 'match-won' && !dbEndTime);

      if (needsMatchUpdate) {
        await updateMatchStatus(
          formattedMatch.id,
          computedStatus,
          computedMatchWinnerId ?? undefined,
          computedEndTime,
        );
      }

      // 12. Update React state from the computed result
      autoSaveEnabled.current = false;
      setMatchState({
        ...computedToMatchState(computed, formattedMatch, tournamentRules),
        serveRotationCooloff: data.serve_rotation_cooloff ?? 5,
      });
      requestAnimationFrame(() => { autoSaveEnabled.current = true; });
      setRecoveryRef(null);

      // 13. Update timeout state
      setActiveTimeout(activeTimeoutResult);
      setCompletedTimeouts(completedTimeoutsResult);
    } catch (err) {
      console.error('[RECOVERY] Error:', err);
      clearMatchRef();
      setRecoveryRef(null);
      throw err;
    } finally {
      setRecoveryLoading(false);
    }
  }, [recoveryRef, teams]);

  const handleExitToTournament = useCallback((tournamentId: string) => {
    // Persist match ref so refresh recovery works when user returns from tournament
    if (matchState.match) {
      saveMatchRef({
        matchId: matchState.match.id,
        teamAId: matchState.teamA?.id ?? '',
        teamBId: matchState.teamB?.id ?? '',
        status: matchState.status,
        tournamentId: matchState.match.tournamentId,
      });
    }
    navigate(`/tournament/${tournamentId}`);
  }, [matchState, navigate]);

  const selectFirstServer = useCallback(async (player: Player) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;

    const firstServingTeamId = getFirstServingTeam(
      matchState.match.tossWonById,
      matchState.match.tossChoice,
      matchState.match.teamAId,
      matchState.match.teamBId
    );

    const servingTeamState = firstServingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

    const validationError = validateFirstServerTeam(player, firstServingTeamId, servingTeamState.name);
    if (validationError) {
      throw new Error(validationError.message);
    }

    try {
      // Check if Set 1 already exists locally or in DB (e.g. after admin reset)
      let existingSet = matchState.sets.find(s => s.setNumber === 1);

      if (!existingSet) {
        const dbSet = await getSetByMatchAndNumber(matchState.match.id, 1);
        if (dbSet) {
          existingSet = {
            id: dbSet.id,
            matchId: dbSet.match_id,
            setNumber: dbSet.set_number,
            scoreA: dbSet.score_a,
            scoreB: dbSet.score_b,
            winnerId: dbSet.winner_id,
            startedAt: dbSet.started_at,
            endedAt: dbSet.ended_at,
            sideChangeShown: dbSet.side_change_shown ?? false,
          };
        }
      }

      let setRecord: { id: string; match_id: string; set_number: number; started_at: string };
      let formattedSet: SetRecord;

      if (existingSet) {
        // Reuse the existing reset set
        setRecord = {
          id: existingSet.id,
          match_id: existingSet.matchId,
          set_number: existingSet.setNumber,
          started_at: existingSet.startedAt,
        };
        formattedSet = {
          ...existingSet,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          endedAt: null,
        };
      } else {
        // Create first set
        const created = await createSet(matchState.match.id, 1);
        setRecord = created;
        formattedSet = {
          id: created.id,
          matchId: created.match_id,
          setNumber: created.set_number,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          startedAt: created.started_at,
          endedAt: null,
        };
      }

      // Update match status to 'live' in database and set start_time if not already set
      await updateMatchStatus(matchState.match.id, 'live');
      if (!matchState.match.startTime) {
        try { await setMatchStartTime(matchState.match.id); } catch { /* non-critical */ }
      }

      // Persist the selected first server so the public scoreboard sees it immediately
      await updateSetServingState(setRecord.id, firstServingTeamId, player.id);

      // Save snapshot for potential undo - first server has no previous rallies
      const previousRotationState = { serveHistory: [], cycleServed: [] };
      serverSelectionSnapshots.current.set(firstServingTeamId, {
        rallyCount: 0,
        previousRotationState,
      });

      setMatchState(prev => {
        const newSets = existingSet
          ? [...prev.sets.filter(s => s.setNumber !== 1), formattedSet]
          : [formattedSet];
        return {
          ...prev,
          currentSetNumber: 1,
          currentSet: {
            scoreA: 0,
            scoreB: 0,
            rallies: [],
            servingTeamId: firstServingTeamId,
            currentServerId: player.id,
            goldenPoint: false,
          },
          status: 'live',
          sets: newSets,
          teamA: prev.teamA ? {
            ...prev.teamA,
            rotationState: firstServingTeamId === prev.teamA.id
              ? { serveHistory: [player.id], cycleServed: [player.id] }
              : { serveHistory: [], cycleServed: [] },
          } : null,
          teamB: prev.teamB ? {
            ...prev.teamB,
            rotationState: firstServingTeamId === prev.teamB.id
              ? { serveHistory: [player.id], cycleServed: [player.id] }
              : { serveHistory: [], cycleServed: [] },
          } : null,
        };
      });
    } catch (err) {
      setError('Failed to start set');
      console.error(err);
      throw err;
    }
  }, [matchState]);

  // Shared helper: after a rally is inserted, fetch fresh DB data, recompute,
  // persist corrections, and update React. Used by recordRally, recordServeFoul,
  // and recordServerFault.
  const recomputeAndApply = useCallback(async () => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;

    const freshMatchData = await getMatchWithDetails(matchState.match.id);
    if (!freshMatchData) throw new Error('Match not found after rally');

    const freshSets: SetRecord[] = (freshMatchData.sets || []).map((s: any) => ({
      id: s.id,
      matchId: s.match_id,
      setNumber: s.set_number,
      scoreA: s.score_a,
      scoreB: s.score_b,
      winnerId: s.winner_id,
      startedAt: s.started_at,
      endedAt: s.ended_at,
      rallies: (s.rallies || []).map((r: any) => ({
        id: r.id,
        setId: r.set_id,
        rallyNumber: r.rally_number,
        servingTeamId: r.serving_team_id,
        serverId: r.server_id,
        pointWonById: r.point_won_by_id,
        lostReason: r.lost_reason as LostReason,
        pointWinnerId: r.point_winner_id,
        recordedAt: r.recorded_at,
        categories: r.categories || [],
        isServerFault: r.is_server_fault || false,
      })).sort((a: Rally, b: Rally) => b.rallyNumber - a.rallyNumber),
      sideChangeShown: s.side_change_shown || false,
      current_serving_team_id: s.current_serving_team_id,
      current_server_id: s.current_server_id,
    }));

    const freshCurrentSet = freshSets.find(s => s.endedAt === null) ?? freshSets[freshSets.length - 1];
    const freshRallies = freshCurrentSet?.rallies ?? [];

    const freshDbServing: DbServingState = {
      servingTeamId: freshCurrentSet ? (freshCurrentSet as any).current_serving_team_id ?? null : null,
      serverId: freshCurrentSet ? (freshCurrentSet as any).current_server_id ?? null : null,
    };

    const recomputeInput: MatchStateInput = {
      match: {
        ...matchState.match,
        status: freshMatchData.status as MatchState['status'],
        winnerId: freshMatchData.winner_id,
        endTime: freshMatchData.end_time,
      },
      teamA: {
        id: matchState.teamA.id,
        name: matchState.teamA.name,
        shortName: matchState.teamA.shortName,
        color: matchState.teamA.color,
        logoUrl: matchState.teamA.logoUrl,
        players: matchState.teamA.players,
      },
      teamB: {
        id: matchState.teamB.id,
        name: matchState.teamB.name,
        shortName: matchState.teamB.shortName,
        color: matchState.teamB.color,
        logoUrl: matchState.teamB.logoUrl,
        players: matchState.teamB.players,
      },
      sets: freshSets,
      currentSetRallies: freshRallies,
      tournamentRules: matchState.tournamentRules ?? null,
      timeouts: [...completedTimeouts, ...(activeTimeout ? [activeTimeout] : [])],
      dbServingState: freshDbServing,
      activeTimeout: activeTimeout ?? null,
    };

    const computed = recomputeMatchState(recomputeInput);

    // Persist corrected values back to DB only if required
    if (freshCurrentSet && computed.currentSet) {
      const needsSetUpdate =
        freshCurrentSet.scoreA !== computed.currentSet.scoreA ||
        freshCurrentSet.scoreB !== computed.currentSet.scoreB ||
        freshCurrentSet.winnerId !== computed.currentSet.setWinnerId ||
        (computed.currentSet.setWinnerId && !freshCurrentSet.endedAt);

      if (needsSetUpdate) {
        await updateSet(freshCurrentSet.id, {
          scoreA: computed.currentSet.scoreA,
          scoreB: computed.currentSet.scoreB,
          winnerId: computed.currentSet.setWinnerId,
          endedAt: computed.currentSet.endedAt,
        });
      }
    }

    const needsMatchUpdate =
      freshMatchData.status !== computed.status ||
      freshMatchData.winner_id !== computed.matchWinnerId ||
      (computed.status === 'match-won' && !freshMatchData.end_time);

    if (needsMatchUpdate) {
      await updateMatchStatus(
        matchState.match.id,
        computed.status,
        computed.matchWinnerId ?? undefined,
        computed.status === 'match-won' ? new Date().toISOString() : undefined,
      );
    }

    // Update React using computedToMatchState()
    setMatchState({
      ...computedToMatchState(computed, recomputeInput.match, matchState.tournamentRules ?? null),
      serveRotationCooloff: matchState.serveRotationCooloff,
    });

    if (computed.status === 'match-won') {
      clearMatchRef();
      capture('match_completed', {
        match_id: recomputeInput.match.id,
        match_number: recomputeInput.match.matchNumber,
        winner_id: computed.matchWinnerId,
        sets_played: computed.setsWonA + computed.setsWonB,
      });
    }
  }, [matchState, activeTimeout, completedTimeouts]);

  const recordRally = useCallback(async (
    winningTeamId: string,
    categories: CategoryAssignment[] = [],
    isServerFault: boolean = false
  ) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    // Prevent double-tap / concurrent submission
    if (rallyInProgress.current) return;
    rallyInProgress.current = true;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    if (!currentSetDb) { rallyInProgress.current = false; return; }

    const currentServingTeamId = matchState.currentSet.servingTeamId;
    const currentServerId = matchState.currentSet.currentServerId;

    try {
      // 1. Insert rally into DB (createRally derives rally_number from DB max — safe against duplicates)
      await createRally({
        setId: currentSetDb.id,
        rallyNumber: matchState.currentSet.rallies.length + 1,
        servingTeamId: currentServingTeamId,
        serverId: currentServerId,
        pointWonById: winningTeamId,
        lostReason: null,
        pointWinnerId: null,
        categories,
        isServerFault,
      });
    } catch (err) {
      // Rally insert failed — safe to surface the error, nothing was written.
      rallyInProgress.current = false;
      setError('Failed to record rally');
      console.error('[recordRally] insert failed:', err);
      throw err;
    }

    // Rally is now committed. Recompute state from DB — retry once on transient failure
    // so a slow post-rally DB round-trip doesn't cause the scorer to re-record.
    try {
      await recomputeAndApply();
    } catch (err) {
      console.error('[recordRally] recomputeAndApply failed, retrying:', err);
      try {
        await recomputeAndApply();
      } catch (retryErr) {
        // Both attempts failed. Rally is in DB — state will heal on next recompute.
        // Do NOT surface "Failed to record rally" — the rally was saved successfully.
        console.error('[recordRally] recomputeAndApply retry also failed:', retryErr);
      }
    } finally {
      rallyInProgress.current = false;
    }

    capture('rally_recorded', {
      match_id: matchState.match.id,
      match_number: matchState.match.matchNumber,
      set_number: matchState.currentSetNumber,
      winning_team_id: winningTeamId,
      serving_team_id: currentServingTeamId,
      is_server_fault: isServerFault,
      has_categories: categories.length > 0,
    });
  }, [matchState, recomputeAndApply]);

  const selectNextServer = useCallback(async (player: Player) => {
    if (!matchState.teamA || !matchState.teamB || !matchState.currentSet || !matchState.match) return;

    const servingTeam = matchState.currentSet.servingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

    // Validate server belongs to serving team (boundary check on user input)
    const teamValidationError = validateServerTeam(player, servingTeam.id, servingTeam.name);
    if (teamValidationError) {
      throw new Error(teamValidationError.message);
    }

    // Validate rotation gap (boundary check on user input)
    if (!checkRotationDeadlock(servingTeam, matchState.serveRotationCooloff)) {
      const validationError = validateRotationGap(player, servingTeam, servingTeam.name, matchState.serveRotationCooloff);
      if (validationError) {
        throw new Error(validationError.message);
      }
    }

    // Save snapshot for potential undo before changing server
    const previousRotationState = { ...servingTeam.rotationState };
    serverSelectionSnapshots.current.set(servingTeam.id, {
      rallyCount: matchState.currentSet.rallies.length,
      previousRotationState,
    });

    try {
      // 1. Persist current_server_id to DB
      const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
      if (currentSetDb) {
        await updateSetServingState(currentSetDb.id, matchState.currentSet.servingTeamId, player.id);
      }

      // 2. Fetch fresh match data from DB
      const freshMatchData = await getMatchWithDetails(matchState.match.id);
      if (!freshMatchData) throw new Error('Match not found after server selection');

      // 3. Parse DB data into MatchStateInput
      const freshSets: SetRecord[] = (freshMatchData.sets || []).map((s: any) => ({
        id: s.id,
        matchId: s.match_id,
        setNumber: s.set_number,
        scoreA: s.score_a,
        scoreB: s.score_b,
        winnerId: s.winner_id,
        startedAt: s.started_at,
        endedAt: s.ended_at,
        rallies: (s.rallies || []).map((r: any) => ({
          id: r.id,
          setId: r.set_id,
          rallyNumber: r.rally_number,
          servingTeamId: r.serving_team_id,
          serverId: r.server_id,
          pointWonById: r.point_won_by_id,
          lostReason: r.lost_reason as LostReason,
          pointWinnerId: r.point_winner_id,
          recordedAt: r.recorded_at,
          categories: r.categories || [],
          isServerFault: r.is_server_fault || false,
        })).sort((a: Rally, b: Rally) => b.rallyNumber - a.rallyNumber),
        sideChangeShown: s.side_change_shown || false,
        current_serving_team_id: s.current_serving_team_id,
        current_server_id: s.current_server_id,
      }));

      const freshCurrentSet = freshSets.find(s => s.endedAt === null) ?? freshSets[freshSets.length - 1];
      const freshRallies = freshCurrentSet?.rallies ?? [];

      const freshDbServing: DbServingState = {
        servingTeamId: freshCurrentSet ? (freshCurrentSet as any).current_serving_team_id ?? null : null,
        serverId: freshCurrentSet ? (freshCurrentSet as any).current_server_id ?? null : null,
      };

      const recomputeInput: MatchStateInput = {
        match: {
          ...matchState.match,
          status: freshMatchData.status as MatchState['status'],
          winnerId: freshMatchData.winner_id,
          endTime: freshMatchData.end_time,
        },
        teamA: {
          id: matchState.teamA.id,
          name: matchState.teamA.name,
          shortName: matchState.teamA.shortName,
          color: matchState.teamA.color,
          logoUrl: matchState.teamA.logoUrl,
          players: matchState.teamA.players,
        },
        teamB: {
          id: matchState.teamB.id,
          name: matchState.teamB.name,
          shortName: matchState.teamB.shortName,
          color: matchState.teamB.color,
          logoUrl: matchState.teamB.logoUrl,
          players: matchState.teamB.players,
        },
        sets: freshSets,
        currentSetRallies: freshRallies,
        tournamentRules: matchState.tournamentRules ?? null,
        timeouts: [...completedTimeouts, ...(activeTimeout ? [activeTimeout] : [])],
        dbServingState: freshDbServing,
        activeTimeout: activeTimeout ?? null,
      };

      // 4. Call recomputeMatchState() — the only source of truth
      const computed = recomputeMatchState(recomputeInput);

      // 5. Persist corrected values back to DB only if required
      if (freshCurrentSet && computed.currentSet) {
        const needsUpdate =
          freshCurrentSet.scoreA !== computed.currentSet.scoreA ||
          freshCurrentSet.scoreB !== computed.currentSet.scoreB ||
          freshCurrentSet.winnerId !== computed.currentSet.setWinnerId ||
          (computed.currentSet.setWinnerId && !freshCurrentSet.endedAt);

        if (needsUpdate) {
          await updateSet(freshCurrentSet.id, {
            scoreA: computed.currentSet.scoreA,
            scoreB: computed.currentSet.scoreB,
            winnerId: computed.currentSet.setWinnerId,
            endedAt: computed.currentSet.endedAt,
          });
        }
      }

      const needsMatchUpdate =
        freshMatchData.status !== computed.status ||
        freshMatchData.winner_id !== computed.matchWinnerId ||
        (computed.status === 'match-won' && !freshMatchData.end_time);

      if (needsMatchUpdate) {
        await updateMatchStatus(
          matchState.match.id,
          computed.status,
          computed.matchWinnerId ?? undefined,
          computed.status === 'match-won' ? new Date().toISOString() : undefined,
        );
      }

      // 6. Update React using computedToMatchState()
      setMatchState({
        ...computedToMatchState(computed, recomputeInput.match, matchState.tournamentRules ?? null),
        serveRotationCooloff: matchState.serveRotationCooloff,
      });
    } catch (err) {
      setError('Failed to select server');
      console.error(err);
      throw err;
    }
  }, [matchState, activeTimeout, completedTimeouts]);

  const recordServeFoul = useCallback(async (server: Player) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) return;
    if (rallyInProgress.current) return;
    rallyInProgress.current = true;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    if (!currentSetDb) { rallyInProgress.current = false; return; }

    const servingTeamId = matchState.currentSet.servingTeamId;
    const isServingTeamA = servingTeamId === matchState.teamA.id;
    const opponentId = isServingTeamA ? matchState.teamB.id : matchState.teamA.id;

    try {
      await createRally({
        setId: currentSetDb.id,
        rallyNumber: matchState.currentSet.rallies.length + 1,
        servingTeamId,
        serverId: server.id,
        pointWonById: opponentId,
        lostReason: 'FOUL',
        pointWinnerId: null,
      });
    } catch (err) {
      rallyInProgress.current = false;
      setError('Failed to record serve foul');
      console.error(err);
      throw err;
    }

    try {
      await recomputeAndApply();
    } catch (err) {
      console.error('[recordServeFoul] recomputeAndApply failed, retrying:', err);
      try { await recomputeAndApply(); } catch (retryErr) {
        console.error('[recordServeFoul] recomputeAndApply retry also failed:', retryErr);
      }
    } finally {
      rallyInProgress.current = false;
    }

    capture('serve_foul_recorded', {
      match_id: matchState.match.id,
      set_number: matchState.currentSetNumber,
      server_id: server.id,
      serving_team_id: servingTeamId,
    });
  }, [matchState, recomputeAndApply]);

  // Server Fault: When serving team wins rally but different player serves (rotation fault)
  const recordServerFault = useCallback(async () => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) return;
    if (rallyInProgress.current) return;
    rallyInProgress.current = true;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    if (!currentSetDb) { rallyInProgress.current = false; return; }

    const servingTeamId = matchState.currentSet.servingTeamId;
    const isServingTeamA = servingTeamId === matchState.teamA.id;
    const opponentId = isServingTeamA ? matchState.teamB.id : matchState.teamA.id;

    try {
      await createRally({
        setId: currentSetDb.id,
        rallyNumber: matchState.currentSet.rallies.length + 1,
        servingTeamId,
        serverId: SERVER_FAULT_PLAYER_ID,
        pointWonById: opponentId,
        lostReason: null,
        pointWinnerId: null,
        isServerFault: true,
      });
    } catch (err) {
      rallyInProgress.current = false;
      setError('Failed to record server fault');
      console.error(err);
      throw err;
    }

    try {
      await recomputeAndApply();
    } catch (err) {
      console.error('[recordServerFault] recomputeAndApply failed, retrying:', err);
      try { await recomputeAndApply(); } catch (retryErr) {
        console.error('[recordServerFault] recomputeAndApply retry also failed:', retryErr);
      }
    } finally {
      rallyInProgress.current = false;
    }

    capture('server_fault_recorded', {
      match_id: matchState.match.id,
      set_number: matchState.currentSetNumber,
      serving_team_id: servingTeamId,
    });
  }, [matchState, recomputeAndApply]);

  // Timeout handlers
  const handleStartTimeout = useCallback(async (teamId: string) => {
    if (!matchState.match) return;

    try {
      const timeout = await createTimeout({
        matchId: matchState.match.id,
        setNumber: matchState.currentSetNumber,
        teamId,
      });
      setActiveTimeout(timeout);
      await updateMatchStatus(matchState.match.id, 'timeout');
      setMatchState(prev => ({
        ...prev,
        status: 'timeout',
        match: prev.match ? { ...prev.match, status: 'timeout' } : null,
      }));
      capture('timeout_started', {
        match_id: matchState.match.id,
        set_number: matchState.currentSetNumber,
        team_id: teamId,
      });
    } catch (err) {
      console.error('[handleStartTimeout] Failed:', err);
      throw err;
    }
  }, [matchState.match, matchState.currentSetNumber]);

  const handleEndTimeout = useCallback(async () => {
    if (!activeTimeout || !matchState.match) return;

    try {
      const ended = await endTimeout(activeTimeout.id);
      setActiveTimeout(null);
      setLastTimeoutDuration(ended.durationSeconds);

      // Re-fetch completed timeouts from DB to ensure count is accurate
      const fresh = await getCompletedTimeouts(matchState.match.id);
      setCompletedTimeouts(fresh);

      // Restore match to live status
      await updateMatchStatus(matchState.match.id, 'live');
      setMatchState(prev => ({
        ...prev,
        status: 'live',
        match: prev.match ? { ...prev.match, status: 'live' } : null,
      }));
      capture('timeout_ended', {
        match_id: matchState.match.id,
        duration_seconds: ended.durationSeconds,
      });
    } catch (err) {
      console.error('[handleEndTimeout] Failed:', err);
      throw err;
    }
  }, [activeTimeout, matchState.match]);

  // Side change handler
  const handleConfirmSideChange = useCallback(async () => {
    if (!matchState.currentSet || !matchState.sets) return;

    const currentSetDb = matchState.sets.find(s => !s.endedAt);
    if (currentSetDb) {
      await updateSetSideChange(currentSetDb.id, true);
      // Immediately reflect in local state so the next recompute reads sideChangeShown = true
      // and does not re-fire the notice before the DB round-trip completes (BUG-M02).
      setMatchState(prev => ({
        ...prev,
        sets: prev.sets.map(s =>
          s.id === currentSetDb.id ? { ...s, sideChangeShown: true } : s
        ),
      }));
    }

    setShowSideChangeNotice(false);
    setSideChangeProcessed(true);
  }, [matchState.currentSet, matchState.sets]);

  // First server team selection handler
  const handleSelectFirstServerTeam = useCallback((teamId: string) => {
    setPendingFirstServerTeamId(teamId);
  }, []);

  const startNewSet = useCallback(async (firstServerId: string, firstServerTeamId?: string) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;

    try {
      const newSetNumber = matchState.currentSetNumber + 1;

      // Check if a set with this number already exists locally (e.g. after admin reset,
      // if recoverMatch did NOT filter it out)
      let existingSet = matchState.sets.find(s => s.setNumber === newSetNumber);

      // If not found locally, check the DB (recoverMatch may have filtered out the reset set)
      if (!existingSet) {
        const dbSet = await getSetByMatchAndNumber(matchState.match.id, newSetNumber);
        if (dbSet) {
          existingSet = {
            id: dbSet.id,
            matchId: dbSet.match_id,
            setNumber: dbSet.set_number,
            scoreA: dbSet.score_a,
            scoreB: dbSet.score_b,
            winnerId: dbSet.winner_id,
            startedAt: dbSet.started_at,
            endedAt: dbSet.ended_at,
            sideChangeShown: dbSet.side_change_shown ?? false,
          };
        }
      }

      let setRecord: { id: string; match_id: string; set_number: number; started_at: string };
      let formattedSet: SetRecord;

      if (existingSet) {
        // Reuse the existing set that was reset
        setRecord = {
          id: existingSet.id,
          match_id: existingSet.matchId,
          set_number: existingSet.setNumber,
          started_at: existingSet.startedAt,
        };
        formattedSet = {
          ...existingSet,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          endedAt: null,
          sideChangeShown: false,
        };
      } else {
        // Create a brand new set
        const created = await createSet(matchState.match.id, newSetNumber);
        setRecord = created;
        formattedSet = {
          id: created.id,
          matchId: created.match_id,
          setNumber: created.set_number,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          startedAt: created.started_at,
          endedAt: null,
          sideChangeShown: false,
        };
      }

      await updateMatchStatus(matchState.match.id, 'live');

      // Derive which team serves first in the new set.
      // Priority: explicit arg > pending state from team-selection modal > loser of last set.
      // The old fallback (servingTeamId of the just-finished set) was wrong — the loser serves.
      const lastCompletedSet = [...matchState.sets]
        .filter(s => s.winnerId !== null)
        .sort((a, b) => b.setNumber - a.setNumber)[0];
      const loserTeamId = lastCompletedSet?.winnerId
        ? (lastCompletedSet.winnerId === matchState.teamA.id ? matchState.teamB.id : matchState.teamA.id)
        : matchState.teamA.id;
      const servingTeamId = firstServerTeamId || pendingFirstServerTeamId || loserTeamId;

      // Persist the first server for the new set immediately
      await updateSetServingState(setRecord.id, servingTeamId, firstServerId);

      // Save snapshot for potential undo - rotation resets for new set
      const previousRotationState = { serveHistory: [], cycleServed: [] };
      serverSelectionSnapshots.current.set(servingTeamId, {
        rallyCount: 0,
        previousRotationState,
      });

      // Reset side change state for new set
      setSideChangeProcessed(false);

      setMatchState(prev => {
        const newSets = existingSet
          ? [...prev.sets.filter(s => s.setNumber !== newSetNumber), formattedSet]
          : [...prev.sets, formattedSet];
        return {
          ...prev,
          currentSetNumber: newSetNumber,
          currentSet: {
            scoreA: 0,
            scoreB: 0,
            rallies: [],
            servingTeamId,
            currentServerId: firstServerId,
            goldenPoint: false,
          },
          sets: newSets,
          status: 'live',
          teamA: prev.teamA ? {
            ...prev.teamA,
            rotationState: servingTeamId === prev.teamA.id
              ? { serveHistory: [firstServerId], cycleServed: [firstServerId] }
              : { serveHistory: [], cycleServed: [] },
          } : null,
          teamB: prev.teamB ? {
            ...prev.teamB,
            rotationState: servingTeamId === prev.teamB.id
              ? { serveHistory: [firstServerId], cycleServed: [firstServerId] }
              : { serveHistory: [], cycleServed: [] },
          } : null,
        };
      });

      // Clear pending team selection
      setPendingFirstServerTeamId(null);

      capture('set_started', {
        match_id: matchState.match.id,
        set_number: newSetNumber,
        serving_team_id: servingTeamId,
      });
    } catch (err) {
      setError('Failed to start new set');
      console.error(err);
      throw err;
    }
  }, [matchState, pendingFirstServerTeamId]);

  const undoLastRally = useCallback(async () => {
    if (undoInProgress.current) return;
    if (!matchState.currentSet || !matchState.sets[matchState.sets.length - 1]) return;

    const lastRally = matchState.currentSet.rallies[0];
    if (!lastRally) return;

    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    undoInProgress.current = true;

    try {
      // 1. Delete the rally from DB
      await deleteRally(currentSetDb.id, lastRally.rallyNumber);

      // 2. Fetch fresh match data from DB (source of truth after deletion)
      const freshMatchData = await getMatchWithDetails(matchState.match.id);
      if (!freshMatchData) throw new Error('Match not found after undo');

      // 3. Parse fresh DB data into MatchStateInput for the recompute engine
      const freshSets: SetRecord[] = (freshMatchData.sets || []).map((s: any) => ({
        id: s.id,
        matchId: s.match_id,
        setNumber: s.set_number,
        scoreA: s.score_a,
        scoreB: s.score_b,
        winnerId: s.winner_id,
        startedAt: s.started_at,
        endedAt: s.ended_at,
        rallies: (s.rallies || []).map((r: any) => ({
          id: r.id,
          setId: r.set_id,
          rallyNumber: r.rally_number,
          servingTeamId: r.serving_team_id,
          serverId: r.server_id,
          pointWonById: r.point_won_by_id,
          lostReason: r.lost_reason as LostReason,
          pointWinnerId: r.point_winner_id,
          recordedAt: r.recorded_at,
          categories: r.categories || [],
          isServerFault: r.is_server_fault || false,
        })).sort((a: Rally, b: Rally) => b.rallyNumber - a.rallyNumber),
        sideChangeShown: s.side_change_shown || false,
        current_serving_team_id: s.current_serving_team_id,
        current_server_id: s.current_server_id,
      }));

      const freshCurrentSet = freshSets.find(s => s.endedAt === null) ?? freshSets[freshSets.length - 1];
      const freshRallies = freshCurrentSet?.rallies ?? [];

      // DB serving columns for the current set
      const freshDbServing: DbServingState = {
        servingTeamId: freshCurrentSet ? (freshCurrentSet as any).current_serving_team_id ?? null : null,
        serverId: freshCurrentSet ? (freshCurrentSet as any).current_server_id ?? null : null,
      };

      // 4. Build the recompute input from fresh DB data
      const recomputeInput: MatchStateInput = {
        match: {
          ...matchState.match,
          status: freshMatchData.status as MatchState['status'],
          winnerId: freshMatchData.winner_id,
          endTime: freshMatchData.end_time,
        },
        teamA: {
          id: matchState.teamA.id,
          name: matchState.teamA.name,
          shortName: matchState.teamA.shortName,
          color: matchState.teamA.color,
          logoUrl: matchState.teamA.logoUrl,
          players: matchState.teamA.players,
        },
        teamB: {
          id: matchState.teamB.id,
          name: matchState.teamB.name,
          shortName: matchState.teamB.shortName,
          color: matchState.teamB.color,
          logoUrl: matchState.teamB.logoUrl,
          players: matchState.teamB.players,
        },
        sets: freshSets,
        currentSetRallies: freshRallies,
        tournamentRules: matchState.tournamentRules ?? null,
        timeouts: [...completedTimeouts, ...(activeTimeout ? [activeTimeout] : [])],
        dbServingState: freshDbServing,
        activeTimeout: activeTimeout ?? null,
      };

      // 5. Run the pure recompute engine
      const computed = recomputeMatchState(recomputeInput);

      // 6. Persist computed state to DB only if values changed (BUG-M01: add guards, remove unconditional writes)
      if (freshCurrentSet && computed.currentSet) {
        const needsSetUpdate =
          freshCurrentSet.scoreA !== computed.currentSet.scoreA ||
          freshCurrentSet.scoreB !== computed.currentSet.scoreB ||
          freshCurrentSet.winnerId !== computed.currentSet.setWinnerId ||
          (computed.currentSet.setWinnerId && !freshCurrentSet.endedAt);

        if (needsSetUpdate) {
          await updateSet(freshCurrentSet.id, {
            scoreA: computed.currentSet.scoreA,
            scoreB: computed.currentSet.scoreB,
            winnerId: computed.currentSet.setWinnerId,
            endedAt: computed.currentSet.endedAt,
          });
        }
      }

      const needsMatchUpdate =
        freshMatchData.status !== computed.status ||
        freshMatchData.winner_id !== computed.matchWinnerId ||
        (computed.status === 'match-won' && !freshMatchData.end_time);

      if (needsMatchUpdate) {
        await updateMatchStatus(
          matchState.match.id,
          computed.status,
          computed.matchWinnerId ?? undefined,
          computed.status === 'match-won' ? new Date().toISOString() : undefined,
        );
      }

      // 7. Update React state from the computed result
      setMatchState({
        ...computedToMatchState(computed, recomputeInput.match, matchState.tournamentRules ?? null),
        serveRotationCooloff: matchState.serveRotationCooloff,
      });
      capture('rally_undone', {
        match_id: matchState.match.id,
        set_number: matchState.currentSetNumber,
      });
    } catch (err) {
      setError('Failed to undo rally');
      console.error(err);
    } finally {
      undoInProgress.current = false;
    }
  }, [matchState, activeTimeout, completedTimeouts]);

  const resetMatch = useCallback(() => {
    setMatchState({
      match: null,
      teamA: null,
      teamB: null,
      currentSetNumber: 0,
      currentSet: null,
      status: 'setup',
      sets: [],
    });
    clearMatchRef();
    setError(null);
    // BUG-25 fix: clear stale timeout state so it doesn't bleed into the next match
    setActiveTimeout(null);
    setCompletedTimeouts([]);
    setLastTimeoutDuration(null);
  }, []);

  const canUndoServerSelection = useCallback((): { canUndo: boolean; servingTeamId: string | null } => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) {
      return { canUndo: false, servingTeamId: null };
    }

    const servingTeamId = matchState.currentSet.servingTeamId;
    const snapshot = serverSelectionSnapshots.current.get(servingTeamId);

    if (!snapshot) {
      return { canUndo: false, servingTeamId: null };
    }

    const currentRallyCount = matchState.currentSet.rallies.length;
    const canUndo = currentRallyCount === snapshot.rallyCount;

    return { canUndo, servingTeamId: canUndo ? servingTeamId : null };
  }, [matchState]);

  const undoServerSelection = useCallback(async () => {
    if (!matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    const { servingTeamId } = canUndoServerSelection();
    if (!servingTeamId) return;

    const snapshot = serverSelectionSnapshots.current.get(servingTeamId);
    if (!snapshot) return;

    const isTeamAServing = servingTeamId === matchState.teamA.id;

    setMatchState(prev => ({
      ...prev,
      currentSet: prev.currentSet ? {
        ...prev.currentSet,
        currentServerId: '',
      } : null,
      teamA: isTeamAServing && prev.teamA ? {
        ...prev.teamA,
        rotationState: snapshot.previousRotationState,
      } : prev.teamA,
      teamB: !isTeamAServing && prev.teamB ? {
        ...prev.teamB,
        rotationState: snapshot.previousRotationState,
      } : prev.teamB,
    }));

    serverSelectionSnapshots.current.delete(servingTeamId);
  }, [matchState, canUndoServerSelection]);

  const servingTeamState = useMemo(() => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) return null;
    return matchState.currentSet.servingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;
  }, [matchState]);

  const currentServer = useMemo(() => {
    if (!matchState.currentSet || !servingTeamState) return null;
    return servingTeamState.players.find(p => p.id === matchState.currentSet?.currentServerId) || null;
  }, [matchState.currentSet, servingTeamState]);

  const eligibleServersForServiceBreak = useMemo(() => {
    if (!servingTeamState) return { eligible: [], ineligible: [] };
    return getEligibleServers(servingTeamState, matchState.serveRotationCooloff);
  }, [servingTeamState, matchState.serveRotationCooloff]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-surface-raised border-t-team-a rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading teams...</p>
        </div>
      </div>
    );
  }

  // Check for Supabase configuration error
  if (configError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Configuration Error</h1>
          <p className="text-text-secondary mb-4">{configError}</p>
          <p className="text-text-muted text-sm">Please check your environment configuration and try again.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-error text-lg mb-4">{error}</p>
          <button
            onClick={() => { setError(null); loadTeams(); }}
            className="px-6 py-3 bg-team-a text-text-primary rounded-lg font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Public routes - accessible without authentication
  const publicRoutes = (
    <Routes>
      <Route path="/live" element={<PublicTournamentCentre />} />
      <Route path="/live/:tournamentId" element={<PublicTournamentCentre />} />
      <Route path="/live/:tournamentId/match/:matchId" element={<PublicTournamentCentre />} />
      <Route path="/player/:playerSlug" element={<PublicPlayerProfile />} />
      <Route path="/overlay/live" element={<BroadcastLiveOverlay />} />
      <Route path="/overlay/:broadcastId" element={<BroadcastOverlay />} />
    </Routes>
  );

  // If accessing public routes, allow through
  if (location.pathname.startsWith('/live') || location.pathname.startsWith('/player')) {
    return <div className="min-h-screen bg-background"><DevBanner />{publicRoutes}</div>;
  }
  if (location.pathname.startsWith('/overlay')) {
    return <div style={{ minHeight: '100vh', background: 'transparent' }}>{publicRoutes}</div>;
  }

  // Login route is public
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <DevBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DevBanner />
      <Routes>
        <Route
          path="/"
          element={
            matchState.status === 'setup' ? (
              <Navigate to="/tournaments" replace />
            ) : matchState.status === 'first-server' ? (
              <FirstServerSelection
                matchState={matchState}
                teams={teams}
                onSelectServer={selectFirstServer}
                onSignOut={signOut}
              />
            ) : matchState.status === 'match-won' ? (
              <MatchComplete
                matchState={matchState}
                teams={teams}
                onNewMatch={resetMatch}
                onSignOut={signOut}
              />
            ) : (
              <LiveScoring
                matchState={matchState}
                servingTeam={servingTeamState}
                currentServer={currentServer}
                eligibleServers={eligibleServersForServiceBreak}
                serverSelectionNeeded={!!matchState.currentSet && !matchState.currentSet.currentServerId}
                onRecordRally={recordRally}
                onSelectNextServer={selectNextServer}
                onUndo={undoLastRally}
                onUndoServerSelection={undoServerSelection}
                canUndoServerSelection={canUndoServerSelection}
                onStartNewSet={startNewSet}
                onRecordServeFoul={recordServeFoul}
                onRecordServerFault={recordServerFault}
                onFetchSetRallies={fetchSetRallies}
                onSignOut={signOut}
                tournamentId={matchState.match?.tournamentId ?? undefined}
                onExitToTournament={matchState.match?.tournamentId ? handleExitToTournament : undefined}
                activeTimeout={activeTimeout}
                onStartTimeout={handleStartTimeout}
                onEndTimeout={handleEndTimeout}
                completedTimeouts={completedTimeouts}
                lastTimeoutDuration={lastTimeoutDuration}
                showSideChangeNotice={showSideChangeNotice}
                onConfirmSideChange={handleConfirmSideChange}
                onSelectFirstServerTeam={handleSelectFirstServerTeam}
              />
            )
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/teams"
          element={
            <ProtectedRoute adminOnly>
              <AdminPanel teams={teams} onDataChanged={reloadTeams} onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/players"
          element={
            <ProtectedRoute adminOnly>
              <AdminPanel teams={teams} onDataChanged={reloadTeams} onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <UserManagement onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/club"
          element={
            <ProtectedRoute adminOnly>
              <ClubSettings onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/broadcast"
          element={
            <ProtectedRoute adminOnly>
              <BroadcastControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/rally-editor"
          element={
            <ProtectedRoute adminOnly>
              <RallyEditor onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments"
          element={<TournamentManagement onSignOut={signOut} />}
        />
        <Route
          path="/tournament/:tournamentId"
          element={<TournamentDashboard onSignOut={signOut} />}
        />
        <Route
          path="/match/:matchId/resume"
          element={
            <ResumeMatchScreen
              matchState={matchState}
              servingTeam={servingTeamState}
              currentServer={currentServer}
              eligibleServers={eligibleServersForServiceBreak}
              teams={teams}
              recoveryLoading={recoveryLoading}
              recoverMatch={recoverMatch}
              resetMatch={resetMatch}
              recordRally={recordRally}
              selectNextServer={selectNextServer}
              undoLastRally={undoLastRally}
              undoServerSelection={undoServerSelection}
              canUndoServerSelection={canUndoServerSelection}
              startNewSet={startNewSet}
              recordServeFoul={recordServeFoul}
              recordServerFault={recordServerFault}
              fetchSetRallies={fetchSetRallies}
              selectFirstServer={selectFirstServer}
              signOut={signOut}
              onExitToTournament={handleExitToTournament}
              activeTimeout={activeTimeout}
              startTimeout={handleStartTimeout}
              endTimeout={handleEndTimeout}
              completedTimeouts={completedTimeouts}
              lastTimeoutDuration={lastTimeoutDuration}
            />
          }
        />
        <Route
          path="/admin/matches"
          element={
            <ProtectedRoute adminOnly>
              <AdminMatchManagement onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scorer"
          element={<ScorerDashboard onSignOut={signOut} />}
        />
        <Route path="/login" element={<Navigate to="/tournaments" replace />} />
        <Route path="*" element={<Navigate to="/tournaments" replace />} />
      </Routes>
    </div>
  );
}

export default App;
